import React, { useState, useEffect, useLayoutEffect, useRef } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import {
  FaFacebookF,
  FaInstagram,
  FaLinkedinIn,
  FaYoutube,
  FaTwitter,
} from "react-icons/fa";
import { FiPhone, FiMail, FiChevronDown, FiMenu, FiX } from "react-icons/fi";
import { useSiteSettings } from "../../context/SiteSettingsContext";
import { publicAPI } from "../../api";
import "./Header.css";

const Header = () => {
  const { settings } = useSiteSettings();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const [news, setNews] = useState([]);
  const [courses, setCourses] = useState([]);
  const dropdownTimeout = useRef(null);
  const headerRef = useRef(null);

  useLayoutEffect(() => {
    const update = () => {
      if (headerRef.current) {
        document.documentElement.style.setProperty(
          "--header-h",
          headerRef.current.offsetHeight + "px"
        );
      }
    };
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, [scrolled]);

  // Fetch news for scrolling ticker + courses for nav dropdowns
  useEffect(() => {
    publicAPI
      .getPublishedNews({ limit: 5 })
      .then((r) => setNews(r.data.data || []))
      .catch(() => {});
    publicAPI
      .getCourses({ is_active: true })
      .then((r) => setCourses(r.data.data || []))
      .catch(() => {});
  }, []);

  // Scroll detection
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileOpen(false);
    setActiveDropdown(null);
  }, [location.pathname]);

  // Dropdown handlers (desktop hover)
  const handleDropdownEnter = (key) => {
    clearTimeout(dropdownTimeout.current);
    setActiveDropdown(key);
  };
  const handleDropdownLeave = () => {
    dropdownTimeout.current = setTimeout(() => setActiveDropdown(null), 200);
  };

  // Mobile dropdown toggle
  const toggleMobileDropdown = (key) => {
    setActiveDropdown((prev) => (prev === key ? null : key));
  };

  const phone = settings.contact_phone || "+91 7676631464";
  const email = settings.contact_email || "info@ptes.edu.in";
  const fbUrl =
    settings.facebook_url ||
    "https://www.facebook.com/peopletreeeducationsociety/";
  const igUrl =
    settings.instagram_url || "https://www.instagram.com/peopletree_college/";
  const liUrl =
    settings.linkedin_url ||
    "https://www.linkedin.com/in/peopletree-college-010b55186/";

  const tweet_url = settings.twitter_url || "https://twitter.com/peopletree6";
  const applyLink = settings.apply_now_link || "/contact";

  // Navigation structure matching the wireframe
  const navItems = [
    { label: "Home", path: "/" },
    {
      label: "Admission",
      path: "https://docs.google.com/forms/d/e/1FAIpQLSeXxeYxGlhQ3YY2JVpiU-n4M_7dDufQMYbtQeRoq7aLlrozMA/viewform",
    },
    {
      label: "About Us",
      path: "/about",
      dropdown: "about",
      children: [
        { label: "Our Story", path: "/about/story" },
        { label: "Vision, Mission", path: "/about" },
        { label: "Leaders Message", path: "/about/leadership" },
        { label: "Organization Chart", path: "/about/organization" },
      ],
    },
    { label: "Wings/Cell", path: "/wings" },
    { label: "Faculty", path: "/faculty" },
    {
      label: "Courses",
      path: "/courses",
      dropdown: "courses",
      children:
        courses.length > 0
          ? courses.map((c) => ({
              label: c.department?.short_name || c.name,
              path: `/courses/${c.slug}`,
            }))
          : [
              { label: "BBA", path: "/courses/bba" },
              { label: "BCA", path: "/courses/bca" },
              { label: "PUC", path: "/courses/pu-commerce" },
            ],
    },
    { label: "Placements", path: "/placements" },
    {
      label: "Gallery",
      path: "/life/gallery",
      dropdown: "gallery",
      children:
        courses.length > 0
          ? courses.map((c) => ({
              label: c.department?.short_name || c.name,
              path: `/life/gallery?dept=${c.department?.slug || ""}`,
            }))
          : [
              { label: "BBA", path: "/life/gallery?dept=bba" },
              { label: "BCA", path: "/life/gallery?dept=bca" },
              { label: "PUC", path: "/life/gallery?dept=puc" },
            ],
    },
    { label: "Library", path: "/library" },
    { label: "Amenities", path: "/amenities" },
    { label: "Feedback", path: "/feedback" },
    { label: "Contact Us", path: "/contact" },
  ];

  // Build scrolling ticker text from news
  const tickerItems = news.map((n) => ({
    text: `[${n.department?.short_name || "PTeS"}] ${n.title}`,
    path: `/life/news/${n.slug}`,
  }));

  return (
    <header ref={headerRef} className={`header ${scrolled ? "header--scrolled" : ""}`}>
      {/* ====== ROW 1: TOPBAR ====== */}
      <div className="topbar">
        <div className="container topbar__inner">
          {/* Left: Social + Contact Info */}
          <div className="topbar__left">
            <div className="topbar__social">
              <a
                href={fbUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="topbar__social-link"
                aria-label="Facebook"
              >
                <FaFacebookF />
              </a>
              <a
                href={igUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="topbar__social-link"
                aria-label="Instagram"
              >
                <FaInstagram />
              </a>

              <a
                href={tweet_url}
                target="_blank"
                rel="noopener noreferrer"
                className="topbar__social-link"
                aria-label="Twitter"
              >
                <FaTwitter />
              </a>

              <a
                href={liUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="topbar__social-link"
                aria-label="LinkedIn"
              >
                <FaLinkedinIn />
              </a>
            </div>
            <span className="topbar__divider" />
            <a href={`mailto:${email}`} className="topbar__info-link">
              <FiMail /> {email}
            </a>
            <span className="topbar__divider" />
            <a
              href={`tel:${phone.replace(/\s/g, "")}`}
              className="topbar__info-link"
            >
              <FiPhone /> {phone}
            </a>
          </div>

          {/* Right: Quick Links */}
          <div className="topbar__right">
            <Link to="/aicte" className="topbar__link">
              AICTE
            </Link>
            <Link to="/code-of-conduct" className="topbar__link">
              Code of Conduct
            </Link>
            <Link to="/alumni" className="topbar__link">
              Alumni
            </Link>
            <a
              href="/admin/login"
              target="_blank"
              rel="noopener noreferrer"
              className="topbar__link topbar__link--admin"
            >
              Admin Portal
            </a>
          </div>
        </div>
      </div>

      {/* ====== ROW 2: SCROLLING ANNOUNCEMENT BAR ====== */}
      {tickerItems.length > 0 && (
        <div className="announcement-bar">
          <div className="announcement-bar__label">Notices</div>
          <div className="announcement-bar__ticker">
            <div className="announcement-bar__track">
              {[...tickerItems, ...tickerItems].map((item, i) => (
                <Link to={item.path} key={i} className="announcement-bar__item">
                  <span className="announcement-bar__dot" />
                  {item.text}
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ====== ROW 3: MAIN NAVIGATION ====== */}
      <nav className={`mainnav ${scrolled ? "mainnav--sticky" : ""}`}>
        <div className="container mainnav__inner">
          {/* Logo */}
          <Link to="/" className="mainnav__logo">
            <img
              src="/Images/PEOPLETREE_Logo.png"
              alt={settings.site_name || "PeopleTree"}
              className="mainnav__logo-img"
              loading="eager"
              decoding="async"
              onError={(e) => {
                e.target.style.display = "none";
              }}
            />
            <div className="mainnav__logo-text">
              <span className="mainnav__logo-name">
                {settings.site_name || "PeopleTree"}
              </span>
              <span className="mainnav__logo-tag">
                {settings.site_tagline || "Growing Intellects"}
              </span>
            </div>
          </Link>

          {/* Desktop Nav Links */}
          <div className="mainnav__links">
            {navItems.map((item) =>
              item.children ? (
                <div
                  key={item.label}
                  className={`mainnav__dropdown ${activeDropdown === item.dropdown ? "mainnav__dropdown--open" : ""}`}
                  onMouseEnter={() => handleDropdownEnter(item.dropdown)}
                  onMouseLeave={handleDropdownLeave}
                >
                  <NavLink
                    to={item.path}
                    className={({ isActive }) =>
                      `mainnav__link mainnav__link--has-dropdown ${isActive ? "mainnav__link--active" : ""}`
                    }
                  >
                    {item.label} <FiChevronDown className="mainnav__chevron" />
                  </NavLink>
                  <div className="mainnav__dropdown-menu">
                    {item.children.map((child) => (
                      <Link
                        key={child.label}
                        to={child.path}
                        className="mainnav__dropdown-item"
                      >
                        {child.label}
                      </Link>
                    ))}
                  </div>
                </div>
              ) : (
                <NavLink
                  key={item.label}
                  to={item.path}
                  end={item.path === "/"}
                  className={({ isActive }) =>
                    `mainnav__link ${isActive ? "mainnav__link--active" : ""}`
                  }
                >
                  {item.label}
                </NavLink>
              ),
            )}
          </div>

          {/* Apply Now CTA */}
          {applyLink.startsWith("http") ? (
            <a
              href={applyLink}
              target="_blank"
              rel="noopener noreferrer"
              className="mainnav__cta"
            >
              APPLY NOW
            </a>
          ) : (
            <Link to={applyLink} className="mainnav__cta">
              APPLY NOW
            </Link>
          )}

          {/* Mobile Toggle */}
          <button
            className="mainnav__toggle"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Toggle navigation"
          >
            {mobileOpen ? <FiX /> : <FiMenu />}
          </button>
        </div>

        {/* Mobile Menu */}
        <div className={`mobilenav ${mobileOpen ? "mobilenav--open" : ""}`}>
          <div className="mobilenav__inner">
            {navItems.map((item) =>
              item.children ? (
                <div key={item.label} className="mobilenav__group">
                  <button
                    className={`mobilenav__link mobilenav__link--parent ${activeDropdown === item.dropdown ? "mobilenav__link--expanded" : ""}`}
                    onClick={() => toggleMobileDropdown(item.dropdown)}
                  >
                    {item.label}{" "}
                    <FiChevronDown className="mobilenav__chevron" />
                  </button>
                  {activeDropdown === item.dropdown && (
                    <div className="mobilenav__children">
                      <Link
                        to={item.path}
                        className="mobilenav__child"
                        onClick={() => setMobileOpen(false)}
                      >
                        View All {item.label}
                      </Link>
                      {item.children.map((child) => (
                        <Link
                          key={child.label}
                          to={child.path}
                          className="mobilenav__child"
                          onClick={() => setMobileOpen(false)}
                        >
                          {child.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <NavLink
                  key={item.label}
                  to={item.path}
                  end={item.path === "/"}
                  className={({ isActive }) =>
                    `mobilenav__link ${isActive ? "mobilenav__link--active" : ""}`
                  }
                  onClick={() => setMobileOpen(false)}
                >
                  {item.label}
                </NavLink>
              ),
            )}
            <div className="mobilenav__quick-links">
              <Link
                to="/aicte"
                className="mobilenav__qlink"
                onClick={() => setMobileOpen(false)}
              >
                AICTE
              </Link>
              <Link
                to="/code-of-conduct"
                className="mobilenav__qlink"
                onClick={() => setMobileOpen(false)}
              >
                Code of Conduct
              </Link>
              <Link
                to="/alumni"
                className="mobilenav__qlink"
                onClick={() => setMobileOpen(false)}
              >
                Alumni
              </Link>
            </div>
            <div className="mobilenav__footer">
              <a
                href={`tel:${phone.replace(/\s/g, "")}`}
                className="mobilenav__footer-link"
              >
                <FiPhone /> {phone}
              </a>
              <a href={`mailto:${email}`} className="mobilenav__footer-link">
                <FiMail /> {email}
              </a>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div className="header__overlay" onClick={() => setMobileOpen(false)} />
      )}
    </header>
  );
};

export default Header;
